import React, { useState, FormEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Send, Mail, Clock, ChevronRight, ChevronDown } from 'lucide-react';
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      emailjs.init('JveHxSEYo9cyKqJ74');
      await emailjs.send('service_p298pqg', 'template_reki4qc', {
        name: formData.get('name'),
        email: formData.get('email'),
        subject: formData.get('subject'),
        message: formData.get('message'),
      });
      setFormStatus({ type: 'success', message: 'Sõnum edukalt saadetud! Võtame teiega peagi ühendust.' });
      form.reset();
    } catch {
      setFormStatus({ type: 'error', message: 'Vabandust, midagi läks valesti. Palun proovige hiljem uuesti.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt | Tõuks.ee</title>
        <meta name="description" content="Võta Tõuks.ee-ga ühendust! Tõukerattad, varuosad, konsultatsioon. Tel: +372 5256424 · ametliktouks@gmail.com · Kiire vastus 1 tööpäeva jooksul." />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/kontakt" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Kontakt — Tõuks.ee',
          url: 'https://xn--tuks-0qa.ee/kontakt',
          description: 'Tõuks.ee kontaktandmed ja kontaktivorm',
          mainEntity: {
            '@type': 'LocalBusiness',
            name: 'Tõuks.ee',
            email: 'ametliktouks@gmail.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Suislepa tn 2',
              addressLocality: 'Jõgeva',
              addressCountry: 'EE',
            },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Kodu', item: 'https://xn--tuks-0qa.ee/' },
            { '@type': 'ListItem', position: 2, name: 'Kontakt', item: 'https://xn--tuks-0qa.ee/kontakt' },
          ],
        })}</script>
      </Helmet>

      <div className="container mx-auto py-12 md:py-20 px-4 md:px-6">
        {/* Page header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Võta meiega ühendust</h1>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Küsimused toodete, tellimuste või kohaletoimetamise kohta? Oleme siin aitamas!
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          {/* Left — contact info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-6">Kontaktandmed</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="bg-[#4FC1E9]/10 rounded-xl p-3 flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#4FC1E9]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">E-post</p>
                    <a href="mailto:ametliktouks@gmail.com" className="text-[#4FC1E9] hover:underline">ametliktouks@gmail.com</a>
                    <p className="text-gray-500 text-sm mt-1">Vastame 1 tööpäeva jooksul</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#4FC1E9]/10 rounded-xl p-3 flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#4FC1E9]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Vastamisaeg</p>
                    <p className="text-gray-700">E–R: 09:00–18:00</p>
                    <p className="text-gray-500 text-sm mt-1">Nädalavahetusel vastame e-kirjadele järgmisel tööpäeval</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="bg-gray-50 rounded-2xl p-6 space-y-3">
              <h3 className="font-bold text-gray-800 mb-4">Kasulikud lingid</h3>
              {[
                { to: '/kohaletoimetamine', label: 'Kohaletoimetamine ja tarneajad' },
                { to: '/garantii-ja-tagastamine', label: 'Tagastamine ja garantii' },
                { to: '/muugitingimused', label: 'Müügitingimused' },
                { to: '/toukeratas', label: 'Vaata kõiki tooteid' },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center justify-between p-3 bg-white rounded-xl hover:border-[#4FC1E9] border border-gray-100 transition-colors group"
                >
                  <span className="text-gray-700 group-hover:text-[#4FC1E9] transition-colors text-sm font-medium">{label}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#4FC1E9] transition-colors" />
                </Link>
              ))}
            </div>
          </div>

          {/* Right — contact form */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 md:p-10 self-start">
            <h2 className="text-2xl font-bold mb-6">Kirjuta meile</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Nimi</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all"
                    placeholder="Sinu nimi"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all"
                    placeholder="sinu@email.ee"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Teema</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all"
                  placeholder="Millega saame aidata?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Sõnum</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent transition-all resize-none"
                  placeholder="Kirjuta oma sõnum siia..."
                />
              </div>

              {formStatus.message && (
                <div className={`p-4 rounded-lg text-sm ${formStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {formStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white font-bold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <Send className="w-5 h-5 mr-2" />
                {isSubmitting ? 'Saadan...' : 'Saada sõnum'}
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* FAQ Section */}
      <div className="container mx-auto pb-16 px-4 md:px-6 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Korduma kippuvad küsimused</h2>
        <p className="text-center text-gray-500 mb-8">Leia kiire vastus — või kirjuta meile allpool.</p>
        <div className="space-y-3">
          {([
            {
              q: 'Kui kiiresti te vastate?',
              a: 'Vastame kõigile e-kirjadele üldjuhul 1 tööpäeva jooksul.',
            },
            {
              q: 'Kuidas saan tellimuse kohta infot?',
              a: 'Pärast tellimuse esitamist saad e-postile kinnituse ja hiljem jälgimiskoodi. Kui e-kiri ei tule, kontrolli spämmikausta. Täpsemat infot saab küsida aadressilt ametliktouks@gmail.com.',
            },
            {
              q: 'Kuidas tagastada toodet?',
              a: 'Sul on 14 kalendripäeva aega kaup tagastada. Saada kiri ametliktouks@gmail.com koos tellimuse numbriga ja kirjeldusega. Aitame edasi!',
            },
          ] as { q: string; a: string }[]).map((item, i) => (
            <FaqItem key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </>
  );
}

function FaqItem({ item, index }: { item: { q: string; a: string }; index: number }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-gray-800 hover:text-[#4FC1E9] transition-colors duration-200"
        aria-expanded={open}
      >
        <span>{item.q}</span>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 ml-4 text-[#4FC1E9] transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-48' : 'max-h-0'}`}>
        <p className="px-6 pb-5 text-gray-600 leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
}
